<template>
  <Teleport :to="portalRootElementSelector">
    <ModalRoot v-model="isOpen" @close="onClose">
      <template #body="{ close }">
        <ModalBodyV2
          class="items-center !max-w-md !px-4 sm:!px-7 !gap-8"
          @close="close"
        >
          <div class="flex flex-col items-center gap-4 self-stretch">
            <Icon
              icon="material-symbols:check-circle-rounded"
              class="text-fill-success size-70u"
            />

            <div class="flex flex-col items-center gap-1">
              <p class="font-bold text-text text-xl text-center">
                {{ $t("channel.auth.webThree.walletConnectedSuccessTitle") }}
              </p>
              <p
                class="font-medium text-text-secondary text-base text-center px-4"
              >
                {{
                  $t("channel.auth.webThree.walletConnectedSuccessDescription")
                }}
              </p>
            </div>

            <CollapsibleRoot
              v-if="tokenBalanceList.length > 0"
              v-model:open="isTokenBalanceCollapsed"
              class="p-4 rounded-3xl flex flex-col gap-3 shrink-0 bg-fill-brand-secondary w-full"
            >
              <CollapsibleTrigger
                class="flex items-center justify-between w-full group gap-2"
              >
                <div class="uppercase font-serif text-base">
                  {{ $t("channel.auth.webThree.availableTokens") }}
                </div>

                <div class="flex items-center gap-2">
                  <span class="font-semibold text-text text-base">
                    {{ $t("channel.auth.webThree.seeMore") }}
                  </span>

                  <span
                    class="rounded-full h-6 w-6 inline-flex items-center justify-center outline-none group-data-[state=closed]:rotate-90 group-data-[state=open]:rotate-180 focus-visible:ring-2 focus-visible:ring-brand-primary"
                  >
                    <Icon icon="ph:caret-up-light" class="h-6 w-6" />
                  </span>
                </div>
              </CollapsibleTrigger>

              <CollapsibleContent
                class="reka-collapsible-content overflow-auto"
              >
                <div
                  v-for="{
                    symbol: token_name,
                    label: token_label,
                    amount,
                  } in tokenBalanceList"
                  :key="token_name"
                  class="flex items-center justify-between gap-2 font-medium text-text text-base"
                >
                  <span>
                    {{
                      $t("channel.auth.webThree.availableTokenLabel", {
                        tokenLabel: token_label,
                      })
                    }}
                  </span>

                  <span>
                    {{ amount }}
                  </span>
                </div>
              </CollapsibleContent>
            </CollapsibleRoot>
          </div>

          <CollapsibleRoot
            v-if="walletService.value?.walletSessionInfo"
            v-model:open="isWalletAddressCollapsed"
            class="p-4 rounded-3xl flex flex-col gap-3 shrink-0 bg-fill-brand-secondary w-full"
          >
            <CollapsibleTrigger
              class="flex items-center justify-between w-full group gap-2"
            >
              <div class="flex flex-col gap-2">
                <span class="uppercase font-serif text-sm text-text-secondary">
                  {{ $t("channel.auth.webThree.connectedWallet") }}
                </span>
              </div>

              <div class="flex items-center gap-2">
                <span class="font-semibold text-text-secondary text-sm">
                  {{ $t("channel.auth.webThree.seeMore") }}
                </span>

                <span
                  class="rounded-full h-6 w-6 inline-flex items-center justify-center outline-none group-data-[state=closed]:rotate-90 group-data-[state=open]:rotate-180 focus-visible:ring-2 focus-visible:ring-brand-primary"
                >
                  <Icon icon="ph:caret-up-light" class="h-6 w-6" />
                </span>
              </div>
            </CollapsibleTrigger>

            <CollapsibleContent class="reka-collapsible-content overflow-auto">
              <div class="flex flex-col gap-2 mt-2">
                <span class="font-mono text-text text-sm break-all">
                  {{ walletService.value.walletSessionInfo?.walletAddress }}
                </span>
              </div>
            </CollapsibleContent>
          </CollapsibleRoot>

          <div class="self-stretch flex flex-col items-stretch gap-2">
            <ButtonV2
              variant="primary"
              size="lg"
              class="!font-sans !font-semibold"
              @click="close"
            >
              {{ $t("channel.auth.webThree.doneButton") }}
            </ButtonV2>
          </div>
        </ModalBodyV2>
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import { portalRootElementSelector } from "@/constants/ui";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { computed, ref } from "vue";
import { useMaybeWalletService } from "@/services/wallet/wallet.service";
import {
  CollapsibleRoot,
  CollapsibleTrigger,
  CollapsibleContent,
} from "reka-ui";
import { Icon } from "@iconify/vue";

const isOpen = defineModel<boolean>({
  required: true,
});
useJustOneOpenModal(isOpen);

const emit = defineEmits<{
  close: [];
}>();

const walletService = useMaybeWalletService();

const tokenBalanceList = computed(() => {
  const list = Array.from(walletService.value?.tokenBalanceMap.values() ?? []);

  // Sort by balance descending
  list.sort((a, b) => {
    return b.amount - a.amount;
  });

  return list;
});

function onClose() {
  emit("close");
}

const isWalletAddressCollapsed = ref(true);
const isTokenBalanceCollapsed = ref(true);
</script>
